import { DigestReviewPanel } from '@/components/digest/digest-review-panel';
import { ContextFilesField } from '@/components/wizard/context-files-field';
import { useDebouncedCallback } from '@/lib/use-debounced-callback';
import type { PatchMatrixInput } from '@mss_creator/shared';
import { Alert, Button, TextArea } from '@pms-digital/design-system';
import { useState } from 'react';

const AUTOSAVE_DEBOUNCE_MS = 500;

interface SujetContexteStepProps {
  matrixId: string;
  /** Documents are fixed once a generation is in flight — the backend enforces this too. */
  documentsLocked?: boolean;
  /** The company is archived (issue #22): the fields are shown, not editable, and nothing is saved. */
  readOnly?: boolean;
  initialSujet: string | null;
  initialContexte: string | null;
  onSave: (patch: PatchMatrixInput) => void;
  onBack: () => void;
  onContinue: () => void;
}

/**
 * Step 2 — sujet & contexte, both blocking (Suivant stays disabled until
 * both are non-empty). No pre-fill from the company: `companies.contexte` is
 * a distinct field, shown only later in the récapitulatif.
 *
 * Context documents live here too, along with the review of their digests
 * (2026-08-26 design doc, D23), and both are deliberately NOT blocking: a
 * matrix generates perfectly well without any document, the digests are
 * produced automatically at generation time if they were not prepared here,
 * and gating "Suivant" on an optional upload would be a trap.
 */
export function SujetContexteStep({
  matrixId,
  documentsLocked = false,
  readOnly = false,
  initialSujet,
  initialContexte,
  onSave,
  onBack,
  onContinue,
}: SujetContexteStepProps) {
  const [sujet, setSujet] = useState(initialSujet ?? '');
  const [contexte, setContexte] = useState(initialContexte ?? '');
  const [touched, setTouched] = useState(false);

  // Two separate debounced callbacks, not one shared between both fields —
  // a single shared debounce would let editing one field cancel/overwrite
  // the other's still-pending save if both are edited within the debounce
  // window (e.g. filling sujet then immediately filling contexte), silently
  // dropping the first field's autosave entirely.
  const debouncedSaveSujet = useDebouncedCallback((value: string) => {
    onSave({ sujet: value });
  }, AUTOSAVE_DEBOUNCE_MS);
  const debouncedSaveContexte = useDebouncedCallback((value: string) => {
    onSave({ contexte: value });
  }, AUTOSAVE_DEBOUNCE_MS);

  const canContinue = sujet.trim().length > 0 && contexte.trim().length > 0;

  function flushAutosave() {
    debouncedSaveSujet.flush();
    debouncedSaveContexte.flush();
  }

  function handleSujetChange(value: string) {
    setSujet(value);
    setTouched(true);
    debouncedSaveSujet(value);
  }

  function handleContexteChange(value: string) {
    setContexte(value);
    setTouched(true);
    debouncedSaveContexte(value);
  }

  const showSujetError = touched && sujet.trim().length === 0;
  const showContexteError = touched && contexte.trim().length === 0;

  return (
    <section className="space-y-6">
      <div>
        <h2 className="font-semibold text-h3">Sujet & contexte</h2>
        <p className="text-gray-600 text-small">
          Les deux champs sont requis pour continuer. Enregistrement automatique.
        </p>
      </div>

      <div>
        <TextArea
          label="Sujet"
          id="matrix-sujet"
          status={showSujetError ? 'error' : 'default'}
          value={sujet}
          readOnly={readOnly}
          onChange={(event) => handleSujetChange(event.target.value)}
          placeholder="Ex : Repositionnement tarifaire de l'offre premium"
          aria-required="true"
          aria-invalid={showSujetError}
        />
        {showSujetError && <Alert tone="danger">Le sujet est requis.</Alert>}
      </div>

      <div>
        <TextArea
          label="Contexte"
          id="matrix-contexte"
          status={showContexteError ? 'error' : 'default'}
          value={contexte}
          readOnly={readOnly}
          onChange={(event) => handleContexteChange(event.target.value)}
          placeholder="Ex : L'entreprise souhaite ajuster son positionnement prix face à l'arrivée de nouveaux concurrents low-cost sur son marché."
          aria-required="true"
          aria-invalid={showContexteError}
        />
        {showContexteError && <Alert tone="danger">Le contexte est requis.</Alert>}
      </div>

      <ContextFilesField matrixId={matrixId} disabled={documentsLocked} />

      {/* The digest gate reads the drafts, not the autosaved values, and
          flushes both debounces before any model call — so the buttons the user
          sees and the 422 the server would answer can never disagree (issue #1). */}
      <DigestReviewPanel
        matrixId={matrixId}
        disabled={documentsLocked}
        sujet={sujet}
        contexte={contexte}
        onBeforeModelCall={flushAutosave}
      />

      <div className="sticky bottom-0 flex justify-between bg-white pt-4">
        <Button type="button" variant="primary" onClick={onBack}>
          Retour
        </Button>
        <Button
          variant="secondary"
          type="button"
          onClick={() => {
            setTouched(true);
            if (!canContinue) return;
            // Flush both fields together on the explicit "leave this step"
            // action, rather than leaving the parent's lifted state (and the
            // eventual backend save) dependent on each field's independent
            // debounce timer having already fired.
            if (!readOnly) onSave({ sujet, contexte });
            onContinue();
          }}
          disabled={!canContinue}
        >
          Suivant
        </Button>
      </div>
    </section>
  );
}
